import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IPractice,
  IVerifonePurchaseRequest,
  IVerifoneRefundRequest,
  IVerifoneTransactionResponse,
  VerifoneTransactionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  getError,
  snapshot,
} from '@principle-theorem/shared';
import { from, race } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { VerifoneService } from '../verifone.service';
import {
  IVerifoneTransactionDialogData,
  IVerifoneTransactionDialogResult,
  VerifoneTransactionDialogComponent,
} from './verifone-transaction-dialog.component';

interface ITransactionCompleted {
  response: IVerifoneTransactionResponse;
}

interface ITransactionCancelledByUser {
  dialogResult: IVerifoneTransactionDialogResult | undefined;
}

type TransactionRaceResult =
  | ITransactionCompleted
  | ITransactionCancelledByUser;

function isTransactionCompleted(
  result: TransactionRaceResult
): result is ITransactionCompleted {
  return 'response' in result;
}

@Injectable()
export class VerifoneTransactionDialogService {
  constructor(
    private _dialog: MatDialog,
    private _verifone: VerifoneService,
    private _snackBar: MatSnackBar
  ) {}

  async executePurchase(
    request: Omit<IVerifonePurchaseRequest, 'referenceId'>
  ): Promise<IVerifoneTransactionResponse | undefined> {
    return this._executeTransaction(request, VerifoneTransactionType.Purchase);
  }

  async executeRefund(
    request: Omit<IVerifoneRefundRequest, 'referenceId'>
  ): Promise<IVerifoneTransactionResponse | undefined> {
    return this._executeTransaction(request, VerifoneTransactionType.Refund);
  }

  private async _executeTransaction(
    request:
      | Omit<IVerifonePurchaseRequest, 'referenceId'>
      | Omit<IVerifoneRefundRequest, 'referenceId'>,
    type: VerifoneTransactionType
  ): Promise<IVerifoneTransactionResponse | undefined> {
    const referenceId = uuid();
    const fullRequest = { ...request, referenceId };

    const dialogRef = this._openProgressDialog(type, request);

    try {
      const transactionPromise =
        type === VerifoneTransactionType.Purchase
          ? this._verifone.purchase(fullRequest as IVerifonePurchaseRequest)
          : this._verifone.refund(fullRequest as IVerifoneRefundRequest);

      // Attach a no-op catch to prevent an unhandled rejection if the dialog
      // closes (user cancels) before the promise settles. The rejection is
      // handled properly inside _waitForTransactionOrCancellation.
      transactionPromise.catch(() => undefined);

      // Race the terminal operation against user cancellation.
      // Terminal operations block until the card is tapped/inserted, so we
      // need to allow the user to cancel while waiting.
      const response = await this._waitForTransactionOrCancellation(
        transactionPromise,
        dialogRef,
        request.practiceRef,
        referenceId,
        request.testModeEnabled
      );

      dialogRef.close();
      return response;
    } catch (error) {
      dialogRef.close();
      throw error;
    }
  }

  private _openProgressDialog(
    type: VerifoneTransactionType,
    request:
      | Omit<IVerifonePurchaseRequest, 'referenceId'>
      | Omit<IVerifoneRefundRequest, 'referenceId'>
  ): MatDialogRef<
    VerifoneTransactionDialogComponent,
    IVerifoneTransactionDialogResult
  > {
    return this._dialog.open<
      VerifoneTransactionDialogComponent,
      IVerifoneTransactionDialogData,
      IVerifoneTransactionDialogResult
    >(VerifoneTransactionDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        type,
        amount: request.amount,
        terminal: request.terminal,
      },
    });
  }

  private async _waitForTransactionOrCancellation(
    transactionPromise: Promise<IVerifoneTransactionResponse>,
    dialogRef: MatDialogRef<
      VerifoneTransactionDialogComponent,
      IVerifoneTransactionDialogResult
    >,
    practiceRef: DocumentReference<IPractice>,
    referenceId: string,
    testModeEnabled?: boolean
  ): Promise<IVerifoneTransactionResponse | undefined> {
    const result: TransactionRaceResult = await snapshot(
      race(
        from(transactionPromise).pipe(map((response) => ({ response }))),
        dialogRef.afterClosed().pipe(map((dialogResult) => ({ dialogResult })))
      )
    );

    if (isTransactionCompleted(result)) {
      return result.response;
    }

    if (result.dialogResult?.cancelled) {
      await this._cancelTransaction(practiceRef, referenceId, testModeEnabled);
    }
  }

  private async _cancelTransaction(
    practiceRef: DocumentReference<IPractice>,
    referenceId: string,
    testModeEnabled?: boolean
  ): Promise<void> {
    try {
      await this._verifone.cancel({
        practiceRef,
        referenceId,
        testModeEnabled,
      });
      this._snackBar.open('Transaction cancelled successfully', 'Close', {
        duration: 3000,
      });
    } catch (error) {
      this._snackBar.open(`Cancellation failed: ${getError(error)}`, 'Close', {
        duration: 5000,
      });
      throw error;
    }
  }
}
